import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { formatEuDateTime } from '../utils/dateFormat';
import './PhoneLookup.css';

function PhoneLookup({ lookupPhoneRequest, onLookupPhone }) {
  const { apiFetch, hasPermission } = useAuth();
  const { language } = useI18n();

  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [selectedDetailTab, setSelectedDetailTab] = useState('conversations');
  const [selectedWalletTransactionId, setSelectedWalletTransactionId] = useState(null);
  const [renamePhoneNumber, setRenamePhoneNumber] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameMessage, setRenameMessage] = useState('');

  const t = useMemo(() => (language === 'da'
    ? {
        title: 'Telefon opslag',
        subtitle: 'Se telefonnumre, ejere og hvem de skriver med',
        placeholder: 'Søg på telefonnummer eller spiller...',
        search: 'Søg',
        searching: 'Søger...',
        results: 'Fandt {count} telefon(er)',
        noResults: 'Ingen telefoner fundet, der matcher "{search}"',
        owner: 'Ejer',
        phoneNumber: 'Telefonnummer',
        battery: 'Batteri',
        setup: 'Opsat',
        assigned: 'Tildelt',
        lastSeen: 'Sidst set',
        threads: 'Samtaler',
        messages: 'Beskeder',
        wallet: 'Wallet',
        participants: 'Kontakter',
        groupChat: 'Gruppesamtale',
        privateChat: 'Privat samtale',
        unknown: 'Ukendt',
        noPhoneSelected: 'Vælg en telefon for at se detaljer',
        noConversations: 'Ingen samtaler fundet',
        messageCount: 'Beskeder',
        lastMessage: 'Sidste besked',
        income: 'Indtægt',
        expense: 'Udgift',
        balance: 'Netto',
        transactions: 'Transaktioner',
        noWalletTransactions: 'Ingen wallet transaktioner fundet'
      }
    : {
        title: 'Phone Lookup',
        subtitle: 'See phone numbers, owners, and who they text',
        placeholder: 'Search by phone number or player...',
        search: 'Search',
        searching: 'Searching...',
        results: 'Found {count} phone(s)',
        noResults: 'No phones found matching "{search}"',
        owner: 'Owner',
        phoneNumber: 'Phone number',
        battery: 'Battery',
        setup: 'Setup',
        assigned: 'Assigned',
        lastSeen: 'Last seen',
        threads: 'Conversations',
        messages: 'Messages',
        wallet: 'Wallet',
        participants: 'Contacts',
        groupChat: 'Group chat',
        privateChat: 'Private chat',
        unknown: 'Unknown',
        noPhoneSelected: 'Select a phone to see details',
        noConversations: 'No conversations found',
        messageCount: 'Messages',
        lastMessage: 'Last message',
        income: 'Income',
        expense: 'Expense',
        balance: 'Balance',
        transactions: 'Transactions',
        noWalletTransactions: 'No wallet transactions found'
      }), [language]);

  const text = (template, params = {}) => template.replace(/\{(\w+)\}/g, (_, key) => params[key] ?? `{${key}}`);

  const handleSearch = useCallback(async (e) => {
    e.preventDefault();
    if (!search.trim()) return;

    setLoading(true);
    setError(null);
    setDetails(null);
    setSelectedPhone(null);
    setSelectedThreadId(null);
    setSelectedDetailTab('conversations');
    setSelectedWalletTransactionId(null);
    setRenamePhoneNumber('');
    setRenameMessage('');

    try {
      const response = await apiFetch(`/api/phones/search?search=${encodeURIComponent(search)}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to search phones');
      setResults(payload.data || []);
    } catch (err) {
      setError(err.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, search]);

  const loadPhoneDetails = useCallback(async (phone) => {
    if (!phone?.phone_number) return;

    setSelectedPhone(phone);
    setSelectedThreadId(null);
    setSelectedDetailTab('conversations');
    setSelectedWalletTransactionId(null);
    setRenamePhoneNumber('');
    setRenameMessage('');
    setDetailsLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/phones/details/${encodeURIComponent(phone.phone_number)}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to load phone details');
      setDetails(payload.data || null);
    } catch (err) {
      setDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    const phoneNumber = String(lookupPhoneRequest?.phoneNumber || '').trim();
    if (!phoneNumber) return;

    const loadRequestedPhone = async () => {
      setSearch(phoneNumber);
      setLoading(true);
      setError(null);
      setDetails(null);
      setSelectedPhone(null);
      setSelectedThreadId(null);
      setSelectedDetailTab('conversations');
      setSelectedWalletTransactionId(null);
      setRenamePhoneNumber('');
      setRenameMessage('');

      try {
        const response = await apiFetch(`/api/phones/search?search=${encodeURIComponent(phoneNumber)}`);
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || 'Failed to search phones');

        const rows = payload.data || [];
        setResults(rows);

        const exactMatch = rows.find((item) => String(item.phone_number || '').trim() === phoneNumber) || rows[0];
        if (exactMatch) {
          await loadPhoneDetails(exactMatch);
        } else {
          await loadPhoneDetails({ phone_number: phoneNumber });
        }
      } catch (err) {
        setError(err.message);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    loadRequestedPhone();
  }, [apiFetch, loadPhoneDetails, lookupPhoneRequest?.phoneNumber, lookupPhoneRequest?.requestId]);

  const phoneCards = results;
  const conversations = details?.conversations || [];
  const walletTransactions = details?.walletTransactions || [];
  const phone = details?.phone || selectedPhone;
  const selectedConversation = conversations.find((conversation) => conversation.channel_id === selectedThreadId) || null;
  const selectedWalletTransaction = walletTransactions.find((transaction) => transaction.id === selectedWalletTransactionId) || null;

  const walletSummary = walletTransactions.reduce((accumulator, transaction) => {
    const amount = Number(transaction.amount) || 0;
    if (amount > 0) accumulator.income += amount;
    if (amount < 0) accumulator.expense += Math.abs(amount);
    accumulator.balance += amount;
    return accumulator;
  }, { income: 0, expense: 0, balance: 0 });

  const renderConversationTitle = (conversation) => {
    if (conversation.is_group) {
      return conversation.name || t.groupChat;
    }

    const otherParticipants = (conversation.participants || []).filter((participant) => participant.phone_number !== phone?.phone_number);
    if (otherParticipants.length === 0) return t.privateChat;

    return otherParticipants.map((participant) => participant.display_name || participant.phone_number).join(', ');
  };

  const renderAttachmentSummary = (attachments) => {
    if (!attachments) return null;

    const parsed = typeof attachments === 'string' ? (() => {
      try {
        return JSON.parse(attachments);
      } catch {
        return null;
      }
    })() : attachments;

    if (!parsed) return null;
    if (Array.isArray(parsed) && parsed.length === 0) return null;
    if (Array.isArray(parsed)) {
      return `${parsed.length} attachment${parsed.length === 1 ? '' : 's'}`;
    }

    return 'Attachment';
  };

  const formatMessageContent = (message) => {
    const content = String(message?.content || '').trim();
    return content.length > 0 ? content : t.unknown;
  };

  const formatMoney = (value) => {
    const amount = Number(value) || 0;
    const prefix = amount > 0 ? '+' : '';
    return `${prefix}${amount.toLocaleString('da-DK')} kr`;
  };

  const formatCompactMoney = (value) => {
    const amount = Number(value) || 0;
    const sign = amount < 0 ? '-' : '';
    const absoluteAmount = Math.abs(amount);

    if (absoluteAmount >= 1000000000) {
      return `${sign}${(absoluteAmount / 1000000000).toFixed(1)} mia. kr`;
    }

    if (absoluteAmount >= 1000000) {
      return `${sign}${(absoluteAmount / 1000000).toFixed(1)} mio. kr`;
    }

    if (absoluteAmount >= 1000) {
      return `${sign}${(absoluteAmount / 1000).toFixed(1)}k kr`;
    }

    return `${sign}${absoluteAmount.toLocaleString('da-DK')} kr`;
  };

  const handleLookupPhone = (phoneNumber) => {
    const normalizedPhoneNumber = String(phoneNumber || '').trim();
    if (!normalizedPhoneNumber) return;
    if (onLookupPhone) onLookupPhone(normalizedPhoneNumber);
  };

  const handleRenamePhoneNumber = useCallback(async (e) => {
    e.preventDefault();

    const currentPhoneNumber = String(details?.phone?.phone_number || '').trim();
    const nextPhoneNumber = String(renamePhoneNumber || '').trim();

    if (!currentPhoneNumber || !nextPhoneNumber) return;
    if (currentPhoneNumber === nextPhoneNumber) {
      setError(language === 'da' ? 'Det nye nummer skal være anderledes.' : 'The new number must be different.');
      return;
    }

    const confirmMessage = language === 'da'
      ? `Er du sikker på, at du vil ændre ${currentPhoneNumber} til ${nextPhoneNumber}?`
      : `Are you sure you want to change ${currentPhoneNumber} to ${nextPhoneNumber}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setRenameLoading(true);
    setRenameMessage('');
    setError(null);

    try {
      const response = await apiFetch('/api/phones/rename-number', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          oldPhoneNumber: currentPhoneNumber,
          newPhoneNumber: nextPhoneNumber
        })
      });

      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to rename phone number');

      setRenamePhoneNumber('');
      setRenameMessage(language === 'da'
        ? `Nummer ændret til ${nextPhoneNumber}`
        : `Number changed to ${nextPhoneNumber}`);

      if (onLookupPhone) {
        onLookupPhone(nextPhoneNumber);
      } else {
        setSearch(nextPhoneNumber);
        await loadPhoneDetails({ phone_number: nextPhoneNumber });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setRenameLoading(false);
    }
  }, [apiFetch, details?.phone?.phone_number, language, loadPhoneDetails, onLookupPhone, renamePhoneNumber]);

  const canRenamePhone = typeof hasPermission === 'function' ? hasPermission('phones.rename') : true;

  const getParticipantPhoneNumber = (conversation, identifier) => {
    const match = (conversation?.participants || []).find((participant) => {
      return participant.phone_number === identifier || participant.display_name === identifier;
    });

    return match?.phone_number || String(identifier || '').trim();
  };

  return (
    <div className="phone-lookup">
      <div className="phone-lookup-header">
        <h2>{t.title}</h2>
        <p>{t.subtitle}</p>
      </div>

      <form className="phone-search-form" onSubmit={handleSearch}>
        <input
          type="text"
          className="phone-search-input"
          placeholder={t.placeholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit" className="phone-search-button" disabled={loading}>
          {loading ? t.searching : t.search}
        </button>
      </form>

      {error && <div className="phone-error">{error}</div>}

      <div className="phone-layout">
        <div className="phone-sidebar">
          <div className="phone-sidebar-header">
            <h3>{text(t.results, { count: phoneCards.length })}</h3>
          </div>

          {loading ? (
            <div className="phone-empty">{t.searching}</div>
          ) : phoneCards.length > 0 ? (
            <div className="phone-card-list">
              {phoneCards.map((item) => (
                <button
                  type="button"
                  key={item.phone_number}
                  className={`phone-card ${selectedPhone?.phone_number === item.phone_number ? 'active' : ''}`}
                  onClick={() => loadPhoneDetails(item)}
                >
                  <div className="phone-card-top">
                    <strong>{item.phone_number}</strong>
                    <span className="phone-battery">{item.battery}%</span>
                  </div>
                  <div className="phone-card-meta">
                    <span>{item.player_name || item.name || item.owner_id || t.unknown}</span>
                    <span>{text(t.threads, { count: item.thread_count || 0 })}</span>
                  </div>
                  <div className="phone-card-meta secondary">
                    <span>{item.is_setup ? t.setup : t.unknown}</span>
                    <span>{item.last_message_at ? formatEuDateTime(item.last_message_at) : t.unknown}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="phone-empty">
              {search ? text(t.noResults, { search }) : t.noPhoneSelected}
            </div>
          )}
        </div>

        <div className="phone-main">
          {!phone ? (
            <div className="phone-placeholder phone-placeholder-hero">
              <div>
                <h3>{t.noPhoneSelected}</h3>
                <p>{t.subtitle}</p>
              </div>
            </div>
          ) : detailsLoading ? (
            <div className="phone-placeholder">{t.searching}</div>
          ) : details ? (
            <>
              <div className="phone-detail-header">
                <div>
                  <a href="#results" className="phone-back-link">&lsaquo; {language === 'da' ? 'Tilbage til telefoner' : 'Back to phones'}</a>
                  <div className="phone-detail-title-row">
                    <button
                      type="button"
                      className="phone-title-button"
                      onClick={() => handleLookupPhone(details.phone.phone_number)}
                    >
                      {details.phone.phone_number}
                    </button>
                    <span className="status-pill active">{language === 'da' ? 'Aktiv' : 'Active'}</span>
                  </div>
                  <p>{details.phone.player_name || details.phone.owner_id || t.unknown}</p>
                </div>
                <div className="phone-detail-stats">
                  <div className="summary-card">
                    <span className="summary-label">{t.owner}</span>
                    <strong>{details.phone.player_name || details.phone.owner_id || t.unknown}</strong>
                    <small>{details.phone.player_identifier || t.unknown}</small>
                  </div>
                  <div className="summary-card">
                    <span className="summary-label">{t.battery}</span>
                    <strong>{details.phone.battery}%</strong>
                    <div className="mini-progress"><span style={{ width: `${Math.max(0, Math.min(100, Number(details.phone.battery) || 0))}%` }} /></div>
                  </div>
                  <div className="summary-card">
                    <span className="summary-label">{t.lastSeen}</span>
                    <strong>{details.phone.last_seen ? formatEuDateTime(details.phone.last_seen) : t.unknown}</strong>
                    <small>{details.phone.assigned ? t.assigned : t.setup}</small>
                  </div>
                </div>
              </div>

              <div className="phone-summary-tags">
                <span className="summary-tag">{details.phone.is_setup ? t.setup : `${t.setup}: ${t.unknown}`}</span>
                <span className="summary-tag">{details.phone.assigned ? t.assigned : `${t.assigned}: ${t.unknown}`}</span>
                <span className="summary-tag">{text(t.threads, { count: conversations.length })}</span>
                <span className="summary-tag">{text(t.transactions, { count: walletTransactions.length })}</span>
                {details.phone.player_identifier && <span className="summary-tag">{details.phone.player_identifier}</span>}
              </div>

              {canRenamePhone && (
                <form className="phone-rename-panel" onSubmit={handleRenamePhoneNumber}>
                  <div className="phone-rename-header">
                    <div>
                      <h3>{language === 'da' ? 'Skift nummer' : 'Change number'}</h3>
                      <p>{language === 'da' ? 'Flytter telefonen og alle tilknyttede data til et nyt nummer.' : 'Moves the phone and all linked data to a new number.'}</p>
                    </div>
                  </div>
                  <div className="phone-rename-row">
                    <input
                      type="text"
                      className="phone-rename-input"
                      value={renamePhoneNumber}
                      onChange={(e) => setRenamePhoneNumber(e.target.value)}
                      placeholder={language === 'da' ? 'Nyt nummer' : 'New number'}
                    />
                    <button type="submit" className="phone-rename-button" disabled={renameLoading || !renamePhoneNumber.trim()}>
                      {renameLoading ? (language === 'da' ? 'Gemmer...' : 'Saving...') : (language === 'da' ? 'Skift nummer' : 'Change number')}
                    </button>
                  </div>
                  {renameMessage && <div className="phone-success">{renameMessage}</div>}
                </form>
              )}

              <div className="phone-convo-shell">
                <div className="conversation-list">
                  <div className="conversation-list-header" id="results">
                    <div className="detail-tab-strip">
                      <button
                        type="button"
                        className={`detail-tab ${selectedDetailTab === 'conversations' ? 'active' : ''}`}
                        onClick={() => setSelectedDetailTab('conversations')}
                      >
                        {t.threads} <span>{conversations.length}</span>
                      </button>
                      <button
                        type="button"
                        className={`detail-tab ${selectedDetailTab === 'wallet' ? 'active' : ''}`}
                        onClick={() => setSelectedDetailTab('wallet')}
                      >
                        {t.wallet} <span>{walletTransactions.length}</span>
                      </button>
                    </div>
                  </div>

                  {selectedDetailTab === 'conversations' ? (
                    conversations.length === 0 ? (
                      <div className="phone-empty phone-empty-inline">{t.noConversations}</div>
                    ) : conversations.map((conversation) => {
                      const isActive = selectedThreadId === conversation.channel_id;

                      return (
                        <button
                          type="button"
                          key={conversation.channel_id}
                          className={`conversation-card conversation-card-button ${isActive ? 'active' : ''}`}
                          onClick={() => setSelectedThreadId(isActive ? null : conversation.channel_id)}
                        >
                          <div className="conversation-header">
                            <div>
                              <strong>{renderConversationTitle(conversation)}</strong>
                              <div className="conversation-subtitle">
                                {conversation.is_group ? t.groupChat : t.privateChat}
                                {' · '}
                                {text(t.participants, { count: conversation.participants?.length || 0 })}
                              </div>
                            </div>
                            <div className="conversation-header-right">
                              <span>{conversation.last_message_timestamp ? formatEuDateTime(conversation.last_message_timestamp) : t.unknown}</span>
                              <span>{conversation.messages?.length || 0} {t.messages}</span>
                            </div>
                          </div>

                          <div className="conversation-preview">
                            <span className="conversation-label">{t.lastMessage}:</span>
                            <span>{conversation.last_message || t.unknown}</span>
                          </div>
                        </button>
                      );
                    })
                  ) : walletTransactions.length === 0 ? (
                    <div className="phone-empty phone-empty-inline">{t.noWalletTransactions}</div>
                  ) : (
                    <div className="wallet-list">
                      <div className="wallet-summary-grid">
                        <div className="summary-card">
                          <span className="summary-label">{t.income}</span>
                          <strong className="wallet-summary-value wallet-positive">{formatCompactMoney(walletSummary.income)}</strong>
                        </div>
                        <div className="summary-card">
                          <span className="summary-label">{t.expense}</span>
                          <strong className="wallet-summary-value wallet-negative">-{formatCompactMoney(walletSummary.expense).replace(/^-/, '')}</strong>
                        </div>
                        <div className="summary-card">
                          <span className="summary-label">{t.balance}</span>
                          <strong className="wallet-summary-value">{formatCompactMoney(walletSummary.balance)}</strong>
                        </div>
                      </div>

                      <div className="wallet-table">
                        {walletTransactions.map((transaction) => {
                          const amount = Number(transaction.amount) || 0;
                          const isPositive = amount >= 0;

                          return (
                            <button
                              type="button"
                              key={transaction.id}
                              className={`wallet-row wallet-row-button ${selectedWalletTransactionId === transaction.id ? 'active' : ''}`}
                              onClick={() => setSelectedWalletTransactionId(selectedWalletTransactionId === transaction.id ? null : transaction.id)}
                            >
                              <div className="wallet-company">
                                {transaction.logo ? <img src={transaction.logo} alt="" className="wallet-logo" /> : <div className="wallet-logo wallet-logo-fallback">{String(transaction.company || '?').charAt(0)}</div>}
                                <div>
                                  <strong>{transaction.company}</strong>
                                  <span>{transaction.timestamp ? formatEuDateTime(transaction.timestamp) : t.unknown}</span>
                                </div>
                              </div>
                              <div className={`wallet-amount ${isPositive ? 'positive' : 'negative'}`}>
                                {formatMoney(amount)}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="conversation-detail-pane">
                  {selectedDetailTab !== 'conversations' ? (
                    <>
                      <div className="conversation-details-header">
                        <div>
                          <h4>{t.wallet}</h4>
                          <p>{language === 'da' ? 'Vælg en transaktion for at se flere detaljer.' : 'Select a transaction to see more details.'}</p>
                        </div>
                        <div className="conversation-details-meta">
                          <span>{text(t.transactions, { count: walletTransactions.length })}</span>
                          <span>{formatMoney(walletSummary.balance)}</span>
                        </div>
                      </div>

                      <div className="message-thread wallet-detail-thread">
                        {!selectedWalletTransaction ? (
                          <div className="phone-empty phone-empty-inline phone-placeholder-detail">
                            {language === 'da' ? 'Vælg en række i wallet listen.' : 'Select a row in the wallet list.'}
                          </div>
                        ) : (
                          <div className="wallet-selected-card">
                            <div className="wallet-selected-top">
                              <div className="wallet-company">
                                {selectedWalletTransaction.logo ? <img src={selectedWalletTransaction.logo} alt="" className="wallet-logo" /> : <div className="wallet-logo wallet-logo-fallback">{String(selectedWalletTransaction.company || '?').charAt(0)}</div>}
                                <div>
                                  <strong>{selectedWalletTransaction.company}</strong>
                                  <span>{selectedWalletTransaction.timestamp ? formatEuDateTime(selectedWalletTransaction.timestamp) : t.unknown}</span>
                                </div>
                              </div>
                              <div className={`wallet-amount ${Number(selectedWalletTransaction.amount) >= 0 ? 'positive' : 'negative'}`}>
                                {formatMoney(selectedWalletTransaction.amount)}
                              </div>
                            </div>

                            <div className="wallet-selected-grid">
                              <div className="summary-card summary-card-phone">
                                <span className="summary-label">{t.phoneNumber}</span>
                                <strong className="phone-number-value">{details.phone.phone_number}</strong>
                              </div>
                              <div className="summary-card">
                                <span className="summary-label">{language === 'da' ? 'Firma' : 'Company'}</span>
                                <strong>{selectedWalletTransaction.company}</strong>
                              </div>
                              <div className="summary-card">
                                <span className="summary-label">{language === 'da' ? 'Tidspunkt' : 'Timestamp'}</span>
                                <strong>{selectedWalletTransaction.timestamp ? formatEuDateTime(selectedWalletTransaction.timestamp) : t.unknown}</strong>
                              </div>
                              <div className="summary-card">
                                <span className="summary-label">{language === 'da' ? 'Transaktions ID' : 'Transaction ID'}</span>
                                <strong>{selectedWalletTransaction.id}</strong>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : !selectedConversation ? (
                    <div className="phone-placeholder phone-placeholder-hero phone-placeholder-detail">
                      <div>
                        <h3>{language === 'da' ? 'Vælg en samtale' : 'Select a conversation'}</h3>
                        <p>{language === 'da' ? 'Den fulde historik vises her.' : 'The full history appears here.'}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="conversation-details-header">
                        <div>
                          <h4>{renderConversationTitle(selectedConversation)}</h4>
                          <p>
                            {selectedConversation.is_group ? t.groupChat : t.privateChat}
                            {' · '}
                            {text(t.participants, { count: selectedConversation.participants?.length || 0 })}
                          </p>
                        </div>
                        <div className="conversation-details-meta">
                          <span>{selectedConversation.messages?.length || 0} {t.messages}</span>
                          <span>{selectedConversation.last_message_timestamp ? formatEuDateTime(selectedConversation.last_message_timestamp) : t.unknown}</span>
                        </div>
                      </div>

                          <div className="participants-row">
                        {(selectedConversation.participants || []).map((participant) => (
                          <button
                            type="button"
                            key={`${selectedConversation.channel_id}-${participant.phone_number}`}
                            className="participant-pill participant-pill-button"
                            onClick={() => handleLookupPhone(participant.phone_number)}
                          >
                            {participant.display_name || participant.phone_number}
                          </button>
                        ))}
                      </div>

                      <div className="message-thread">
                        {(selectedConversation.messages || []).length > 0 ? (
                          <div className="message-list">
                            {(selectedConversation.messages || []).map((message) => {
                              const attachmentLabel = renderAttachmentSummary(message.attachments);
                              const isOutgoing = String(message.sender || '').trim() === String(phone?.phone_number || '').trim();
                                  const senderPhoneNumber = getParticipantPhoneNumber(selectedConversation, message.sender);

                              return (
                                <div key={message.id} className={`message-item ${isOutgoing ? 'outgoing' : 'incoming'}`}>
                                  <div className="message-meta">
                                        <button
                                          type="button"
                                          className="message-sender-button"
                                          onClick={() => handleLookupPhone(senderPhoneNumber)}
                                        >
                                          {message.sender_display_name || message.sender}
                                        </button>
                                    <span>{message.timestamp ? formatEuDateTime(message.timestamp) : t.unknown}</span>
                                  </div>
                                  <div className="message-content">{formatMessageContent(message)}</div>
                                  {attachmentLabel && <div className="message-attachments">{attachmentLabel}</div>}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="phone-empty phone-empty-inline">{t.noConversations}</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="phone-placeholder">{t.noPhoneSelected}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PhoneLookup;
